import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../../../request/http-common';

const AdminJointAccountTransactions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState(searchParams.get('account') || 'all');
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionComment, setActionComment] = useState('');

  const { data: transactionsData, isLoading, error, refetch } = useQuery(
    ['adminJointAccountTransactions', statusFilter, typeFilter, accountFilter, searchTerm],
    async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (accountFilter !== 'all') params.append('joint_account_id', accountFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await apiClient.get(`/api/admin/joint-accounts/transactions?${params}`);
      return response.data;
    }
  );

  const { data: pendingTransactions } = useQuery(
    'adminPendingJointTransactions',
    async () => {
      const response = await apiClient.get('/api/admin/joint-accounts/transactions?status=pending');
      return response.data;
    }
  );

  const { mutate: approveTransaction, isLoading: isApproving } = useMutation(
    async ({ transactionId, action, comment }) => {
      if (action === 'approve') {
        const response = await apiClient.post(`/api/admin/joint-transactions/${transactionId}/force-approve`);
        return response.data;
      } else {
        const response = await apiClient.post(`/api/joint-transactions/${transactionId}/approve`, {
          action: 'rejected',
          comment
        });
        return response.data;
      }
    },
    {
      onSuccess: () => {
        setActionDialogOpen(false);
        setSelectedTransaction(null);
        setActionComment('');
        refetch();
      },
      onError: (error) => {
        console.error('Failed to process transaction:', error);
      }
    }
  );

  const { mutate: adminApproveTransaction, isLoading: isAdminApproving } = useMutation(
    async ({ transactionId, action, adminNotes }) => {
      const response = await apiClient.post(`/api/admin/joint-transactions/${transactionId}/approve`, {
        action,
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setActionDialogOpen(false);
        setSelectedTransaction(null);
        setActionComment('');
        refetch();
      },
      onError: (error) => {
        console.error('Failed to process admin transaction action:', error);
        alert('Failed to process transaction: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'approved': return 'info';
      default: return 'default';
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <TrendingUpIcon sx={{ color: '#10B981' }} />;
      case 'withdrawal': return <TrendingDownIcon sx={{ color: '#F84960' }} />;
      case 'investment': return <AccountBalanceIcon sx={{ color: '#3B82F6' }} />;
      default: return <ScheduleIcon sx={{ color: '#6C7293' }} />;
    }
  };

  const handleTransactionAction = (transaction, action) => {
    setSelectedTransaction(transaction);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedTransaction || !actionType) return;
    
    // Use the new admin approval mutation for all transaction statuses
    adminApproveTransaction({
      transactionId: selectedTransaction.id,
      action: actionType === 'approve' ? 'approved' : 'rejected',
      adminNotes: actionComment
    });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 1) {
      setStatusFilter('pending');
    } else {
      setStatusFilter('all');
    }
  };

  const transactions = currentTab === 1 ? 
    pendingTransactions?.transactions?.data || [] : 
    transactionsData?.transactions?.data || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton 
          onClick={() => navigate('/admin/joint-accounts')}
          sx={{ color: '#F7931A' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#FFFFFF', flex: 1 }}>
          Joint Account Transactions
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 48, color: '#FFB800', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {pendingTransactions?.transactions?.data?.length || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Pending Approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 700, mb: 1 }}>
                {transactionsData?.transactions?.data?.filter(t => t.transaction_type === 'deposit').length || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Deposits Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingDownIcon sx={{ fontSize: 48, color: '#F84960', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#F84960', fontWeight: 700, mb: 1 }}>
                {transactionsData?.transactions?.data?.filter(t => t.transaction_type === 'withdrawal').length || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Withdrawals Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {transactionsData?.transactions?.data?.filter(t => t.status === 'completed').length || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Completed Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ 
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        mb: 3
      }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: '#6C7293',
              '&.Mui-selected': { color: '#F7931A' }
            },
            '& .MuiTabs-indicator': { backgroundColor: '#F7931A' }
          }}
        >
          <Tab label="All Transactions" />
          <Tab 
            label={`Pending Approval (${pendingTransactions?.transactions?.data?.length || 0})`}
          />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Card sx={{
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        mb: 3
      }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#6C7293', mr: 1 }} />,
                  style: { color: '#FFFFFF' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={currentTab === 1}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="deposit">Deposits</MenuItem>
                  <MenuItem value="withdrawal">Withdrawals</MenuItem>
                  <MenuItem value="investment">Investments</MenuItem>
                  <MenuItem value="transfer">Transfers</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                >
                  <MenuItem value="all">All Accounts</MenuItem>
                  {/* Add specific accounts here if needed */}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                fullWidth
                sx={{ borderColor: '#F7931A', color: '#F7931A' }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card sx={{
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139'
      }}>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              Failed to load transactions
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Transaction</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Account</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Initiator</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Approvals</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id} sx={{ '&:hover': { backgroundColor: 'rgba(247, 147, 26, 0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getTransactionIcon(transaction.transaction_type)}
                          <Box>
                            <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>
                              {transaction.transaction_type?.charAt(0).toUpperCase() + transaction.transaction_type?.slice(1)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6C7293' }}>
                              {transaction.reference || `#${transaction.id}`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                          {transaction.joint_account?.account_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6C7293' }}>
                          #{transaction.joint_account?.account_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ 
                          color: transaction.transaction_type === 'deposit' ? '#10B981' : '#F84960',
                          fontWeight: 600
                        }}>
                          {transaction.transaction_type === 'deposit' ? '+' : '-'}{formatPrice(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                          {transaction.initiator?.name || transaction.initiator?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status}
                          color={getStatusColor(transaction.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {transaction.approvals && transaction.approvals.length > 0 ? (
                            transaction.approvals.filter(a => a.action === 'approved').map((approval, index) => (
                              <Chip
                                key={index}
                                label={`${approval.approver?.name || 'Unknown'}: Approved`}
                                color="success"
                                size="small"
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" sx={{ color: '#6C7293' }}>
                              No approvals
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#6C7293' }}>
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6C7293' }}>
                          {new Date(transaction.created_at).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            onClick={() => navigate(`/admin/joint-accounts/${transaction.joint_account_id}`)}
                            sx={{ color: '#3B82F6' }}
                            size="small"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          
                          {/* Show approval/decline buttons for all statuses except completed and rejected */}
                          {!['completed', 'rejected'].includes(transaction.status) && (
                            <>
                              <IconButton
                                onClick={() => handleTransactionAction(transaction, 'approve')}
                                sx={{ color: '#10B981' }}
                                size="small"
                                title="Approve Transaction"
                              >
                                <CheckCircleIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => handleTransactionAction(transaction, 'reject')}
                                sx={{ color: '#F84960' }}
                                size="small"
                                title="Reject Transaction"
                              >
                                <CancelIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', borderBottom: '1px solid #2B3139' }}>
          {actionType === 'approve' ? 'Approve Transaction' : 'Reject Transaction'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedTransaction && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                Transaction Details:
              </Typography>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)',
                p: 2,
                borderRadius: '8px',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Type: {selectedTransaction.transaction_type}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Amount: {formatPrice(selectedTransaction.amount)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Account: {selectedTransaction.joint_account?.account_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Initiator: {selectedTransaction.initiator?.name || selectedTransaction.initiator?.email}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Status: {selectedTransaction.status}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Reference: {selectedTransaction.reference || `#${selectedTransaction.id}`}
                </Typography>
              </Box>
              
              {/* Show existing approvals */}
              {selectedTransaction.approvals && selectedTransaction.approvals.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 1, fontWeight: 600 }}>
                    Existing Approvals:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedTransaction.approvals.map((approval, index) => (
                      <Chip
                        key={index}
                        label={`${approval.approver?.name || 'Unknown'}: ${approval.action}`}
                        color={approval.action === 'approved' ? 'success' : 'error'}
                        size="small"
                        sx={{ 
                          fontSize: '0.7rem',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          
          <TextField
            fullWidth
            label={actionType === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason'}
            value={actionComment}
            onChange={(e) => setActionComment(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={isAdminApproving || (actionType === 'reject' && !actionComment.trim())}
              variant="contained"
              sx={{
                background: actionType === 'approve' ? 
                  'linear-gradient(135deg, #10B981 0%, #059669 100%)' :
                  'linear-gradient(135deg, #F84960 0%, #DC2626 100%)'
              }}
            >
              {isAdminApproving ? <CircularProgress size={20} /> : `${actionType === 'approve' ? 'Approve' : 'Reject'} Transaction`}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminJointAccountTransactions; 